/**
 * BackgroundStreamNotifier - Shows toast notifications for all BSS lifecycle events:
 *   started    → INFO  "Generation is running in the background"
 *   actionNeeded / failed → INFO or ERROR depending on message content
 *   complete   → SUCCESS "Project created and files uploaded"
 *
 * Rendered once in App.js alongside GlobalToastNotifications.
 */

import { useEffect, useRef } from 'react';
import { useActiveScriptGeneration } from '../../store/generation/generationStore';
import { useToastNotificationContext } from './ToastNotificationContext';

const BackgroundStreamNotifier = () => {
  const activeSession = useActiveScriptGeneration();
  const { showInfo, showSuccess, showError } = useToastNotificationContext();

  // Keyed refs so we only fire each notification once per conversation / message
  const startedRef    = useRef(null);
  const actionRef     = useRef(null);
  const completedRef  = useRef(null);

  // ── 1. GENERATION STARTED ──────────────────────────────────────────────────
  // Fires once when a new session appears and generation hasn't completed yet.
  useEffect(() => {
    if (!activeSession?.conversationId || activeSession?.backgroundComplete) return;

    const key = activeSession.conversationId;
    if (startedRef.current === key) return;
    startedRef.current = key;

    const projectName = activeSession.projectName || 'Script Generation';
    const returnUrl   = `/project-chat/${activeSession.conversationId}`;
    showInfo(projectName, 'Generation is running in the background.', returnUrl);
  }, [
    activeSession?.conversationId,
    activeSession?.backgroundComplete,
    activeSession?.projectName,
    showInfo,
  ]);

  // ── 2. ACTION NEEDED / FAILED ──────────────────────────────────────────────
  // Fires whenever backgroundComplete flips true with actionNeeded = true.
  // Covers: stream errors, project creation failures, and intermediate action steps.
  useEffect(() => {
    if (!activeSession?.backgroundComplete || !activeSession?.actionNeeded) return;

    // Include actionMessage in the key so re-triggering on a new error still notifies.
    const notifyKey = `${activeSession.conversationId}-${activeSession.actionMessage}`;
    if (actionRef.current === notifyKey) return;
    actionRef.current = notifyKey;

    const projectName = activeSession.projectName || 'Script Generation';
    const message     = activeSession.actionMessage || 'Action required. Return to continue.';
    const returnUrl   = `/project-chat/${activeSession.conversationId}`;

    const isError = /failed|error/i.test(message);
    if (isError) {
      showError(projectName, message, returnUrl);
    } else {
      showInfo(projectName, message, returnUrl);
    }
  }, [
    activeSession?.backgroundComplete,
    activeSession?.actionNeeded,
    activeSession?.conversationId,
    activeSession?.actionMessage,
    activeSession?.projectName,
    showInfo,
    showError,
  ]);

  // ── 3. GENERATION COMPLETE ─────────────────────────────────────────────────
  // Fires when backgroundComplete = true AND actionNeeded = false (project created).
  useEffect(() => {
    if (!activeSession?.backgroundComplete || activeSession?.actionNeeded) return;

    const notifyKey = `${activeSession.conversationId}-complete`;
    if (completedRef.current === notifyKey) return;
    completedRef.current = notifyKey;

    const projectName = activeSession.projectName || 'Script Generation';
    const message     = activeSession.actionMessage || 'Project created successfully!';
    const returnUrl   = `/project-chat/${activeSession.conversationId}`;
    showSuccess(projectName, message, returnUrl);
  }, [
    activeSession?.backgroundComplete,
    activeSession?.actionNeeded,
    activeSession?.conversationId,
    activeSession?.projectName,
    activeSession?.actionMessage,
    showSuccess,
  ]);

  return null;
};

export default BackgroundStreamNotifier;
